import { AlertCircle, Library as LibraryIcon, Search, SearchX } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type ReactNode, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmModal } from '@/components/patterns/ConfirmModal';
import { EmptyStateBlock } from '@/components/patterns/EmptyStateBlock';
import { MultiSelectDropdown } from '@/components/patterns/MultiSelectDropdown';
import { StoryCard } from '@/components/patterns/StoryCard';
import { SeriesLibraryCard } from '@/components/patterns/SeriesLibraryCard';
import { CatalogWorldCard } from '@/components/patterns/CatalogWorldCard';
import { ContentContainer } from '@/components/shell/ContentContainer';
import { useWorldSelectionSheet } from '@/components/worlds/useWorldSelectionSheet';
import { Input } from '@/components/ui/input';
import { listChildren } from '@/features/children/api';
import { deleteStory, fetchStoriesList, patchStoryFavorite } from '@/features/story/api';
import type { StoryListItem } from '@/features/story/types';
import { fetchSeriesList } from '@/features/series/api';
import type { Series } from '@/features/series/types';
import { useSeriesEnabled } from '@/hooks/useSeriesEnabled';
import { fetchCatalogLibrary } from '@/features/catalog/api';
import type { CatalogLibraryCard } from '@/features/catalog/types';
import { useCatalogEnabled } from '@/hooks/useCatalogEnabled';
import { fetchWorlds } from '@/features/worlds/api';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { clearStoryCountCache } from '@/hooks/useStoryCount';
import { ApiError, NetworkError } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import { WORLDS, type WorldSlug } from '@/lib/worlds';
import { STRINGS } from '@/strings/strings';

/**
 * Sentinel value used by MultiSelectDropdown's `allOption` for the
 * Library Year/Month/Child filters. Kept in-sync with the literal
 * passed to each dropdown's `allOption.value` prop below. Filter
 * computations (yearValue/monthValue) MUST treat this as "no filter"
 * — `Number(ALL_OPTION_VALUE)` is NaN, which the backend rejects
 * with `validation_error` (Bug-Fix Commit on top of Batch 8).
 */
const ALL_OPTION_VALUE = '__ALL__';

/**
 * Coerce a Set-of-strings dropdown selection to a finite number,
 * returning null for the empty-set, multi-select, or `__ALL__`
 * sentinel cases. Centralizes the NaN-guard that the original
 * Batch 8 code missed (`Number('__ALL__')` returns NaN, which the
 * backend rejects with 400 `validation_error`).
 */
function selectionToFiniteNumber(s: Set<string>): number | null {
  if (s.size !== 1) return null;
  const raw = Array.from(s)[0];
  if (raw === ALL_OPTION_VALUE) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

const DEFAULT_PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;
const FAVORITE_TOAST_AUTO_DISMISS_MS = 3000;
const DELETE_TITLE_TRUNCATE = 60;

/** Truncate long story titles for the delete-confirm modal so the
 *  body line wrapping stays balanced (Refinement 2). */
function truncateTitleForDeleteModal(title: string): string {
  if (title.length <= DELETE_TITLE_TRUNCATE) return title;
  return title.slice(0, DELETE_TITLE_TRUNCATE) + '…';
}

/**
 * Year-dropdown options at Mai-Launch — hardcoded current-year and
 * the 2 prior years. Tracker entry covers post-launch dynamic-range
 * derivation from the user's earliest story (PWA/Offen — Batch 8
 * Commit 5 closing).
 */
function getYearOptions(): Array<{ value: string; label: string }> {
  const currentYear = new Date().getFullYear();
  return [currentYear, currentYear - 1, currentYear - 2].map((y) => ({
    value: String(y),
    label: String(y),
  }));
}

type LoadError = 'network' | 'load' | null;

interface ChildOption {
  id: string;
  name: string;
}

export function LibraryRoute() {
  const navigate = useNavigate();
  // Batch 10 Sub-phase D — Library-empty CTA opens the global
  // WorldSelectionSheet instead of navigating to the (deleted) bare
  // /builder route. Decision #13 Path A.
  const { openSheet } = useWorldSelectionSheet();
  const [items, setItems] = useState<StoryListItem[] | null>(null);
  const [total, setTotal] = useState<number>(0);
  const [loadError, setLoadError] = useState<LoadError>(null);

  // Filter-Bar state (Batch 8 Commit 2). `worldIds` holds the
  // backend UUIDs (NOT slugs) — Bug-Fix Commit on top of Batch 8:
  // the backend `ListStoriesQueryDto.worldIds` validator is
  // `@IsUUID('4')`, so synthetic slugs like `world-aa` are rejected.
  // The slug→UUID map is built once on mount via fetchWorlds().
  const [worldIds, setWorldIds] = useState<Set<string>>(new Set());
  const [worldSlugMap, setWorldSlugMap] = useState<Map<WorldSlug, string>>(new Map());
  const [favoriteOnly, setFavoriteOnly] = useState(false);
  const [childIds, setChildIds] = useState<Set<string>>(new Set());
  const [yearSel, setYearSel] = useState<Set<string>>(new Set());
  const [monthSel, setMonthSel] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);

  const [childOptions, setChildOptions] = useState<ChildOption[]>([]);

  // Worlds list for slug→UUID resolution at filter time. Failure is
  // silent — chip-clicks become no-ops until the map populates,
  // matching the children-list behaviour. Single fetch on mount.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const worlds = await fetchWorlds();
        if (cancelled) return;
        const m = new Map<WorldSlug, string>();
        for (const w of worlds) {
          if (w.slug === 'aa' || w.slug === 'sb' || w.slug === 'kp') {
            m.set(w.slug, w.id);
          }
        }
        setWorldSlugMap(m);
      } catch {
        // Best-effort.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Batch 8 Commit 4 — Delete + favorite-toggle + infinite-scroll state
  const [deleteTarget, setDeleteTarget] = useState<StoryListItem | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [favoriteToast, setFavoriteToast] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const S = STRINGS.library;

  // D7: series are grouped into ONE tile in EVERY view (Alle + world filters +
  // the Serien pill), not only the Serien filter. The flat story list excludes
  // series-episode stories backend-side (Story.seriesId), so we fetch the series
  // list once the feature is enabled and render the grouped tiles in all views.
  const { enabled: seriesEnabled } = useSeriesEnabled();
  const [seriesMode, setSeriesMode] = useState(false);
  const [seriesItems, setSeriesItems] = useState<Series[] | null>(null);
  const [seriesError, setSeriesError] = useState<string | null>(null);

  useEffect(() => {
    if (!seriesEnabled) return;
    let cancelled = false;
    void (async () => {
      // Resets live inside the async callback (not the synchronous effect
      // body) so the "loading" reset doesn't cascade-render per the
      // react-hooks/set-state-in-effect rule.
      setSeriesItems(null);
      setSeriesError(null);
      try {
        const res = await fetchSeriesList();
        if (!cancelled) setSeriesItems(res.items);
      } catch {
        if (!cancelled) setSeriesError(STRINGS.series.season.loadFailed);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [seriesEnabled]);

  // Block C2 — Wissenswelten catalog cards, mirroring the series fetch above.
  // Gated by useCatalogEnabled(): when off, NO /api/catalog call is made and the
  // feed is byte-identical to today. Failure is silent (the merge just omits
  // catalog cards) — the library must never break on a catalog hiccup.
  const { enabled: catalogEnabled } = useCatalogEnabled();
  const [catalogItems, setCatalogItems] = useState<CatalogLibraryCard[] | null>(null);

  useEffect(() => {
    if (!catalogEnabled) return;
    let cancelled = false;
    void (async () => {
      setCatalogItems(null);
      try {
        const cards = await fetchCatalogLibrary();
        if (!cancelled) setCatalogItems(cards);
      } catch {
        if (!cancelled) setCatalogItems([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [catalogEnabled]);

  // Children list for the Kinder-Filter — fetched once on mount.
  // Failure is silent: the dropdown shows only "Alle Kinder".
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const list = await listChildren();
        if (cancelled) return;
        setChildOptions(list.map((c) => ({ id: c.id, name: c.name })));
      } catch {
        // Best-effort.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // selectionToFiniteNumber filters out the `__ALL__` sentinel +
  // any non-finite Number coercion (Bug-Fix Commit on top of Batch 8
  // — the original code did a raw `Number(...)` which produced NaN
  // for `__ALL__` and failed backend validation with 400).
  const yearValue = selectionToFiniteNumber(yearSel);
  const monthValue = yearValue !== null ? selectionToFiniteNumber(monthSel) : null;

  const queryParams = useMemo(() => {
    const params: Parameters<typeof fetchStoriesList>[0] = {
      limit: DEFAULT_PAGE_SIZE,
    };
    if (worldIds.size > 0) params.worldIds = Array.from(worldIds);
    // Drop the `__ALL__` sentinel from childIds — same backend
    // `@IsUUID('4', { each: true })` validator that rejects synthetic
    // strings (Bug-Fix Commit on top of Batch 8).
    const childIdsArr = Array.from(childIds).filter((id) => id !== ALL_OPTION_VALUE);
    if (childIdsArr.length > 0) params.childIds = childIdsArr;
    if (yearValue !== null) params.year = yearValue;
    if (monthValue !== null) params.month = monthValue;
    if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
    if (favoriteOnly) params.isFavorite = true;
    return params;
  }, [worldIds, childIds, yearValue, monthValue, debouncedSearch, favoriteOnly]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadError(null);
      try {
        const res = await fetchStoriesList(queryParams);
        if (cancelled) return;
        setItems(res.items);
        setTotal(res.total);
        setLoadError(null);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof NetworkError) setLoadError('network');
        else if (err instanceof ApiError) setLoadError('load');
        else setLoadError('load');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [queryParams]);

  const errorMessage =
    loadError === 'network'
      ? S.errors.networkError
      : loadError === 'load'
        ? S.errors.loadFailed
        : null;

  const isLoading = items === null && !errorMessage;

  // Discriminator (Gate-1 plan verbatim) — drives Library-empty vs
  // Filter-empty branch when items.length === 0. `yearValue !== null`
  // was added in the Batch 8 Library bug-fix commit (2026-04-26):
  // selecting year=2025 with no other filter set was previously
  // mis-classified as "no filter" and triggered the Library-empty
  // CTA instead of the Filter-empty "Filter zurücksetzen" CTA.
  const isFiltered =
    worldIds.size > 0 ||
    childIds.size > 0 ||
    yearValue !== null ||
    monthValue !== null ||
    debouncedSearch.trim() !== '' ||
    favoriteOnly;

  /**
   * "Filter zurücksetzen" CTA: returns ALL filter state to a clean
   * (no-filter) baseline. Year resets to **unset** — same as initial
   * mount. Asymmetry from Batch 8 Commit 3/4 (year=currentYear on
   * reset) was reversed in the Batch 8 Library bug-fix commit
   * (2026-04-26): the new `isFiltered` discriminator includes
   * `yearValue !== null`, so year=currentYear after reset would mean
   * `isFiltered=true` even after an explicit reset, contradicting
   * the user mental model of "reset = clean state".
   */
  const handleResetFilters = () => {
    setWorldIds(new Set());
    setChildIds(new Set());
    setYearSel(new Set());
    setMonthSel(new Set());
    setSearch('');
    setFavoriteOnly(false);
  };

  // ── Batch 8 Commit 4 handlers ──

  const handleToggleFavorite = async (item: StoryListItem) => {
    const newValue = !item.isFavorite;
    // Optimistic update
    setItems(
      (prev) => prev?.map((s) => (s.id === item.id ? { ...s, isFavorite: newValue } : s)) ?? null,
    );
    setFavoriteToast(null);
    try {
      await patchStoryFavorite(item.id, newValue);
    } catch {
      // Revert + toast (Flag 10 ADDITIONAL — auto-dismiss after 3s)
      setItems(
        (prev) =>
          prev?.map((s) => (s.id === item.id ? { ...s, isFavorite: item.isFavorite } : s)) ?? null,
      );
      setFavoriteToast(S.errors.favoriteToggleFailed);
    }
  };

  // Auto-dismiss the favorite-toggle toast after 3s. Re-arm timer if
  // the toast text changes (rapid clicks → new error → reset timer
  // rather than stack toasts).
  useEffect(() => {
    if (!favoriteToast) return;
    const id = window.setTimeout(() => setFavoriteToast(null), FAVORITE_TOAST_AUTO_DISMISS_MS);
    return () => window.clearTimeout(id);
  }, [favoriteToast]);

  const handleDeleteRequest = (item: StoryListItem) => {
    setDeleteError(null);
    setDeleteTarget(item);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    const target = deleteTarget;
    try {
      await deleteStory(target.id);
      setItems((prev) => prev?.filter((s) => s.id !== target.id) ?? null);
      setTotal((t) => Math.max(0, t - 1));
      clearStoryCountCache();
      setDeleteTarget(null);
      setDeleteError(null);
    } catch (err) {
      if (err instanceof NetworkError) {
        setDeleteError(S.errors.networkError);
      } else {
        setDeleteError(S.errors.deleteFailed);
      }
    }
  };

  // ── Infinite-scroll wiring ──
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const hasMore = items !== null && items.length < total;

  const loadMore = async () => {
    if (loadingMore || !items) return;
    setLoadingMore(true);
    try {
      const res = await fetchStoriesList({ ...queryParams, offset: items.length });
      setItems((prev) => (prev ? [...prev, ...res.items] : res.items));
      setTotal(res.total);
    } catch {
      // Silent — last-known list stays visible. User can retry by
      // scrolling away + back (next intersection re-fires loadMore).
    } finally {
      setLoadingMore(false);
    }
  };

  useInfiniteScroll(triggerRef, () => void loadMore(), hasMore);

  // D7/#4: render one grouped series ROW (full-width, single "Staffel öffnen").
  // Shared by the Serien filter and the interleaved Alle + world views.
  const seriesCard = (s: Series) => {
    let slug: WorldSlug | null = null;
    for (const [sl, uuid] of worldSlugMap) {
      if (uuid === s.worldId) slug = sl;
    }
    const resolved = slug ?? resolveWorldSlug(s.worldId);
    // B3: full world name ("Abenteuer Alltag" / "Kuschelpatrouille"), matching
    // single-story library cards, instead of the "AA"/"KP" short label.
    const worldLabel = WORLDS[resolved.toUpperCase() as Uppercase<WorldSlug>].name;
    const childLabel = childOptions.find((c) => c.id === s.childId)?.name ?? null;
    return (
      <SeriesLibraryCard
        series={s}
        worldLabel={worldLabel}
        childLabel={childLabel}
        createdDate={formatGermanShortDate(s.createdAt ?? '')}
        onOpen={() => navigate(`/series/${s.id}`)}
      />
    );
  };

  // Block C2 — catalog (Wissenswelt) card, interleaved like a series card.
  const catalogCard = (c: CatalogLibraryCard) => (
    <CatalogWorldCard
      card={c}
      unlockedDate={formatGermanShortDate(c.lastUnlockedAt)}
      onOpen={() => navigate(`/catalog/${c.worldSlug}`)}
    />
  );

  // Single-story row (extracted so it can interleave with series rows).
  const storyRow = (item: StoryListItem) => {
    let slugFromMap: WorldSlug | null = null;
    for (const [s, uuid] of worldSlugMap) {
      if (uuid === item.worldId) slugFromMap = s;
    }
    const slug = slugFromMap ?? resolveWorldSlug(item.worldId);
    const worldName = WORLDS[slug.toUpperCase() as Uppercase<WorldSlug>].name;
    return (
      <StoryCard
        density="library"
        title={item.title}
        childName={item.childName}
        worldName={worldName}
        worldSlug={slug}
        createdDate={formatGermanShortDate(item.createdAt)}
        previewText={item.previewText}
        isFavorite={item.isFavorite}
        audioReady={item.audioStatus === 'completed'}
        pdfReady={item.pdfStatus === 'ready'}
        audioStatus={item.audioStatus}
        pdfStatus={item.pdfStatus}
        refunded={item.refunded}
        onClick={() => navigate(`/story/${item.id}`)}
        onToggleFavorite={() => void handleToggleFavorite(item)}
        onAudioClick={() => navigate(`/story/${item.id}?play=1`)}
        onPdfClick={() => navigate(`/story/${item.id}?pdf=1`)}
        onDeleteClick={() => handleDeleteRequest(item)}
        testId={`library-card-${item.id}`}
      />
    );
  };

  // D7: series shown in Alle + world views (not favorites), honoring the active
  // world + child filters. The flat list already excludes their episode stories.
  const seriesChildFilter = Array.from(childIds).filter((id) => id !== ALL_OPTION_VALUE);
  const visibleSeries =
    !favoriteOnly && seriesItems
      ? seriesItems.filter((s) => {
          if (worldIds.size > 0 && !worldIds.has(s.worldId)) return false;
          if (seriesChildFilter.length > 0 && !seriesChildFilter.includes(s.childId ?? ''))
            return false;
          return true;
        })
      : [];

  // Block C2: catalog cards appear in the "Alle" view only (no favorites, no
  // series mode, no story-world chip active — catalog worlds are not story-world
  // chips, so a chip filter excludes them). Empty when catalog is disabled.
  const visibleCatalog =
    !favoriteOnly && !seriesMode && worldIds.size === 0 && catalogItems ? catalogItems : [];

  // #4: interleave series rows + story rows in ONE chronological list (newest
  // first). Story pagination loads newest-first; series are all loaded. C2 adds
  // catalog cards, sorted by lastUnlockedAt into the same chronological feed.
  const mergedFeed: { key: string; el: ReactElement }[] =
    !seriesMode && items !== null
      ? [
          ...items.map((it) => ({ key: `story-${it.id}`, date: it.createdAt, el: storyRow(it) })),
          ...visibleSeries.map((s) => ({
            key: `series-${s.id}`,
            date: s.createdAt ?? '',
            el: seriesCard(s),
          })),
          ...visibleCatalog.map((c) => ({
            key: `catalog-${c.worldSlug}`,
            date: c.lastUnlockedAt,
            el: catalogCard(c),
          })),
        ]
          .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0))
          .map(({ key, el }) => ({ key, el }))
      : [];

  // ── World-chip handlers ──
  // Resolves slug to backend UUID via the worldSlugMap. Click is a
  // no-op while the map is still loading (initial mount race). The
  // user can re-click after worlds-fetch resolves; in practice the
  // fetch completes well before any user interaction.
  const toggleWorld = (slug: WorldSlug) => {
    const id = worldSlugMap.get(slug);
    if (!id) return;
    const next = new Set(worldIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setWorldIds(next);
  };

  const isWorldChipActive = (slug: WorldSlug): boolean => {
    const id = worldSlugMap.get(slug);
    return id ? worldIds.has(id) : false;
  };

  return (
    <div className="min-h-screen bg-dream-white">
      <ContentContainer maxWidth="default">
        <div className="py-6">
          <h1 className="text-[22px] font-semibold text-dream-navy">{S.title}</h1>
          {items !== null && !errorMessage && (
            <p className="mt-1 mb-6 text-sm text-dream-navy/60" data-testid="library-subtitle">
              {S.subtitleTemplate.replace('{count}', String(total))}
            </p>
          )}

          {/* Search input */}
          <div className="relative mb-3">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-dream-navy/50"
              strokeWidth={2}
              aria-hidden="true"
            />
            <Input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={S.searchPlaceholder}
              className="h-10 pl-9 bg-dream-white text-dream-navy"
              data-testid="library-search-input"
            />
          </div>

          {/* World chips */}
          <div
            role="group"
            aria-label={S.chips.ariaLabel}
            className="mb-3 flex flex-wrap items-center gap-2"
            data-testid="library-world-chips"
          >
            <FilterChip
              active={!seriesMode && worldIds.size === 0 && !favoriteOnly}
              onClick={() => {
                setSeriesMode(false);
                setWorldIds(new Set());
                setFavoriteOnly(false);
              }}
              testId="library-chip-all"
            >
              {S.chips.all}
            </FilterChip>
            <FilterChip
              active={!seriesMode && isWorldChipActive('aa')}
              onClick={() => {
                setSeriesMode(false);
                toggleWorld('aa');
              }}
              testId="library-chip-aa"
            >
              {WORLDS.AA.shortName}
            </FilterChip>
            <FilterChip
              active={!seriesMode && isWorldChipActive('sb')}
              onClick={() => {
                setSeriesMode(false);
                toggleWorld('sb');
              }}
              testId="library-chip-sb"
            >
              {WORLDS.SB.shortName}
            </FilterChip>
            <FilterChip
              active={!seriesMode && isWorldChipActive('kp')}
              onClick={() => {
                setSeriesMode(false);
                toggleWorld('kp');
              }}
              testId="library-chip-kp"
            >
              {WORLDS.KP.shortName}
            </FilterChip>
            <FilterChip
              active={!seriesMode && favoriteOnly}
              onClick={() => {
                setSeriesMode(false);
                setFavoriteOnly((v) => !v);
              }}
              testId="library-chip-favorites"
            >
              {S.chips.favorites}
            </FilterChip>
            {seriesEnabled && (
              <FilterChip
                active={seriesMode}
                onClick={() => {
                  setSeriesMode(true);
                  setWorldIds(new Set());
                  setFavoriteOnly(false);
                }}
                testId="library-chip-series"
              >
                {STRINGS.series.library.pill}
              </FilterChip>
            )}
          </div>

          {/* Year / Month / Child dropdowns */}
          <div className="mb-6 flex flex-wrap items-center gap-2">
            <MultiSelectDropdown<string>
              options={getYearOptions()}
              selected={yearSel}
              onChange={(s) => {
                setYearSel(s);
                if (s.size === 0) setMonthSel(new Set());
              }}
              mode="single"
              allOption={{ label: S.yearFilter.allLabel, value: ALL_OPTION_VALUE }}
              triggerLabel={S.yearFilter.triggerLabel}
              testId="library-year-filter"
            />
            <MultiSelectDropdown<string>
              options={S.monthFilter.months.map((label, idx) => ({
                value: String(idx + 1),
                label,
              }))}
              selected={monthSel}
              onChange={(s) => {
                setMonthSel(s);
                // Bug-Fix Commit on top of Batch 8: month requires
                // year (backend constraint — `month` without `year`
                // is dropped by the queryParams memo). When the user
                // picks a real month with no year (or "Alle Jahre"),
                // auto-set year to currentYear so the filter actually
                // takes effect.
                const monthIsReal = s.size === 1 && !s.has(ALL_OPTION_VALUE);
                const yearIsReal = selectionToFiniteNumber(yearSel) !== null;
                if (monthIsReal && !yearIsReal) {
                  setYearSel(new Set([String(new Date().getFullYear())]));
                }
              }}
              mode="single"
              allOption={{ label: S.monthFilter.allLabel, value: ALL_OPTION_VALUE }}
              triggerLabel={S.monthFilter.triggerLabel}
              testId="library-month-filter"
            />
            <MultiSelectDropdown<string>
              options={childOptions.map((c) => ({ value: c.id, label: c.name }))}
              selected={childIds}
              onChange={setChildIds}
              mode="multi"
              allOption={{ label: S.childFilter.allLabel, value: ALL_OPTION_VALUE }}
              triggerLabel={S.childFilter.triggerLabel}
              testId="library-child-filter"
            />
          </div>

          {errorMessage && (
            <div
              role="alert"
              data-testid="library-error-banner"
              className="flex items-start gap-2 mb-4 p-3 rounded-md bg-error/10 border border-error/30 text-error-text text-sm"
            >
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{errorMessage}</span>
            </div>
          )}

          {/* B3: Serien mode — grouped series cards from GET /series. */}
          {seriesMode && (
            <>
              {seriesError && (
                <div
                  role="alert"
                  className="mb-4 flex items-start gap-2 rounded-md border border-error/30 bg-error/10 p-3 text-sm text-error-text"
                >
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{seriesError}</span>
                </div>
              )}
              {seriesItems === null && !seriesError && (
                <ul className="flex flex-col gap-2" aria-busy="true">
                  {[0, 1, 2].map((i) => (
                    <li key={i} className="h-32 rounded-lg bg-dream-navy/5 animate-pulse" />
                  ))}
                </ul>
              )}
              {seriesItems !== null && seriesItems.length === 0 && (
                <p className="py-8 text-center text-sm text-dream-navy/70">
                  {S.filterEmpty.heading}
                </p>
              )}
              {seriesItems !== null && seriesItems.length > 0 && (
                <ul className="flex flex-col gap-2" data-testid="library-series-grid">
                  {seriesItems.map((s) => (
                    <li key={s.id}>{seriesCard(s)}</li>
                  ))}
                </ul>
              )}
            </>
          )}

          {!seriesMode && isLoading && (
            <ul
              className="flex flex-col gap-2"
              data-testid="library-loading-skeleton"
              aria-busy="true"
            >
              {[0, 1, 2].map((i) => (
                <li key={i} className="h-24 rounded-lg bg-dream-navy/5 animate-pulse" />
              ))}
            </ul>
          )}

          {!seriesMode && items !== null && mergedFeed.length > 0 && (
            <>
              <ul className="flex flex-col gap-2" data-testid="library-list">
                {mergedFeed.map((entry) => (
                  <li key={entry.key}>{entry.el}</li>
                ))}
              </ul>
              {/* Infinite-scroll trigger — only renders when more
                  items are available than currently loaded. */}
              {hasMore && (
                <div
                  ref={triggerRef}
                  className="text-center text-xs text-dream-navy/60 py-4"
                  data-testid="library-scroll-trigger"
                >
                  {loadingMore ? S.scrollTrigger.loading : S.scrollTrigger.idle}
                </div>
              )}
            </>
          )}

          {/* 4 Briefing-spec'd states: Library-empty / Filter-empty
              branch on `isFiltered`. The List + Loading + Error states
              above own their own data-states; these blocks render
              ONLY when the fetch resolved with zero items. */}
          {!seriesMode &&
            items !== null &&
            mergedFeed.length === 0 &&
            !errorMessage &&
            !isFiltered && (
              <EmptyStateBlock
                icon={<LibraryIcon className="h-10 w-10 text-dream-navy/40" aria-hidden="true" />}
                heading={S.empty.heading}
                body={S.empty.body}
                cta={{ label: S.empty.cta, onClick: openSheet }}
                testId="library-empty-block"
              />
            )}

          {!seriesMode &&
            items !== null &&
            mergedFeed.length === 0 &&
            !errorMessage &&
            isFiltered && (
              <EmptyStateBlock
                icon={<SearchX className="h-10 w-10 text-dream-navy/40" aria-hidden="true" />}
                heading={S.filterEmpty.heading}
                body={S.filterEmpty.body}
                cta={{ label: S.filterEmpty.cta, onClick: handleResetFilters }}
                testId="library-filter-empty-block"
              />
            )}
        </div>
      </ContentContainer>

      {/* Delete-Story-Confirm-Modal — Briefing line 683-687 verbatim,
          ConfirmModal Mode A with destructive variant. */}
      <ConfirmModal
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteTarget(null);
            setDeleteError(null);
          }
        }}
        headline={S.deleteModal.headline}
        body={S.deleteModal.bodyTemplate.replace(
          '{title}',
          deleteTarget ? truncateTitleForDeleteModal(deleteTarget.title) : '',
        )}
        primaryCta={S.deleteModal.primaryCta}
        secondaryCta={S.deleteModal.secondaryCta}
        variant="destructive"
        onConfirm={() => void handleDeleteConfirm()}
        testId="delete-story-confirm-modal"
      />

      {/* Delete-error inline banner (after the modal closes; modal
          itself doesn't render the error to avoid mid-modal layout
          shifts). Stays visible until the next user action. */}
      {deleteError && (
        <div
          role="alert"
          data-testid="library-delete-error-banner"
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-start gap-2 p-3 rounded-md bg-error/95 border border-error text-dream-white text-sm shadow-lg"
        >
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{deleteError}</span>
        </div>
      )}

      {/* Favorite-toggle revert toast — auto-dismisses after 3s. */}
      {favoriteToast && (
        <div
          role="status"
          aria-live="polite"
          data-testid="library-favorite-toast"
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 p-3 rounded-md bg-dream-navy/90 text-dream-white text-sm shadow-lg"
        >
          {favoriteToast}
        </div>
      )}
    </div>
  );
}

// ── Route-private FilterChip ──

interface FilterChipProps {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
  testId?: string;
}

/**
 * Inline route-private chip for the Library world-filter row.
 * Promote to `components/patterns/` if a 2nd consumer surfaces (e.g.
 * Inspiration filter, Library-of-Tips filter).
 *
 * Active state: Sky-Blue chip background per Briefing visual + mockup
 * `chip-ac` class. Inactive: secondary background.
 */
function FilterChip({ active, onClick, children, testId }: FilterChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      data-testid={testId}
      className={cn(
        'inline-flex h-8 items-center rounded-full px-3 text-xs font-medium transition-colors focus-visible:outline-2 focus-visible:outline-dream-navy/50',
        active
          ? 'bg-world-aa text-dream-navy'
          : 'bg-dream-navy/10 text-dream-navy hover:bg-dream-navy/15',
      )}
    >
      {children}
    </button>
  );
}

// ── Route-private debounce hook ──

/**
 * Small inline useDebounce — Mai-Launch single-consumer (Library
 * search). Promote to `pwa/src/hooks/` if a 2nd consumer surfaces.
 */
function useDebounce<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState<T>(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

// ── World-id resolver helpers ──

/**
 * Backend stores world as a UUID. PWA renders 3 known slugs at
 * Mai-Launch. Best-effort suffix-match works against the seed-data
 * UUID convention. Promote to a shared lib helper when a 2nd
 * surface needs the same resolution.
 */
function resolveWorldSlug(worldId: string): WorldSlug {
  const lc = worldId.toLowerCase();
  if (lc.endsWith('aa') || lc.includes('-aa-') || lc.includes('alltagsabenteuer')) return 'aa';
  if (lc.endsWith('sb') || lc.includes('-sb-') || lc.includes('schlauberger')) return 'sb';
  if (lc.endsWith('kp') || lc.includes('-kp-') || lc.includes('kuschelpatrouille')) return 'kp';
  return 'aa';
}

function formatGermanShortDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(d);
}
